/**
 * buy-credits-dialog.tsx — Hộp "Mua lượt AI": mỗi gói "10.500 lượt AI" + giá (lượt theo giá trị nạp, đã gồm tặng thêm; khách
 * không thấy ≈ token / đ giá trị ví). Chọn gói → tạm tính, VAT, tổng → tạo đơn và
 * chuyển sang cổng thanh toán (dùng lại luồng /billing: EvoPay / kích hoạt ngay).
 * Kết quả thanh toán theo dõi ở /billing (PaymentReturn) như đơn gói dịch vụ.
 */
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, ShieldCheck } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useBillingMethods, type PaymentMethod } from '@/hooks/use-billing'
import {
  useAiCreditCheckout, useAiCreditQuote, useAiCredits, useCreditPacks,
  useCanBuyAiCredits,
} from '@/hooks/use-ai-credits'
import {
  aiErrorMessage, amountText, formatDong, formatTurns, packBonusPercent, packTotals, packTurns, tokenRateOf,
  type CreditPack,
} from '@/lib/ai-credits'
import { METHOD_COPY, currentPageForReturn, goToPayment, resultPath, writePendingOrder } from '@/pages/billing/payment-methods'
import { MethodLogos } from '@/pages/billing/payment-logos'
import { isPromoBlocked, vatLabel } from '@/pages/billing/plans'
import { PromoCodeBox, pickMethod } from '@/pages/billing/upgrade-page'

export function BuyCreditsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation('aiOps')
  // Quyền mua lượt AI theo vai trò động (ai_credits.create) — không theo tên vai trò.
  const canPay = useCanBuyAiCredits()
  const packsQ = useCreditPacks(open)
  const { data: credits } = useAiCredits()
  const methods = useBillingMethods()
  const [picked, setPicked] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)
  // Mã ưu đãi — chỉ mã có phạm vi "Credit AI" áp được (server kiểm, lỗi ở quote.promoError).
  const [promoCode, setPromoCode] = useState('')
  const checkout = useAiCreditCheckout()

  const packs = packsQ.data?.packs ?? []
  const rate = tokenRateOf(credits)
  const pack: CreditPack | undefined = packs.find((p) => p.id === picked) ?? packs[1] ?? packs[0]
  const quote = useAiCreditQuote(open && pack ? pack.id : null, promoCode || undefined)
  const local = pack ? packTotals(pack, packsQ.data?.vatPercent) : null
  // Số thật theo báo giá server; chưa có → tạm tính phía client.
  const q = quote.data
  const subtotal = q?.vat?.preTax ?? local?.subtotal ?? 0
  const vat = q?.vat?.amount ?? local?.vat ?? 0
  const vatPercent = q?.vat?.percent ?? local?.vatPercent ?? 10
  const total = q?.total ?? local?.total ?? 0
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod | null>(null)
  const methodId = pickMethod(methods.data?.methods ?? [], chosenMethod)
  const method = methods.data?.methods.find((m) => m.id === methodId) ?? null
  const busy = checkout.isPending || redirecting
  const promoBlocked = isPromoBlocked(promoCode, q ?? undefined)
  const checkingPromo = !!promoCode && (quote.isFetching || quote.isPlaceholderData)

  function pay() {
    if (!pack) return
    // Trang đang mở hộp nạp credit → sau khi trả xong, nút "Tiếp tục" ở trang kết quả đưa khách về đúng đây.
    const from = currentPageForReturn()
    checkout.mutate(
      { packId: pack.id, method: method?.id, returnTo: resultPath(from), ...(q?.promo ? { promoCode: q.promo.code } : {}) },
      {
        onSuccess: (r) => {
          // Gửi lại cùng Idempotency-Key với đơn đã trả → server trả { status: 'paid', replay: true }.
          if (r.method === 'credit' || (r as { status?: string }).status === 'paid') {
            toast.success(t('buyCredits.added', { amount: formatTurns(packTurns(pack, rate)) }))
            onOpenChange(false)
            return
          }
          writePendingOrder(r.orderId, from)
          setRedirecting(true)
          try {
            goToPayment(r)
          } catch (err) {
            setRedirecting(false)
            writePendingOrder(null)
            toast.error(err instanceof Error ? err.message : t('buyCredits.gatewayError'))
          }
        },
        onError: (err) => toast.error(aiErrorMessage(err)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('buyCredits.title')}</DialogTitle>
          <DialogDescription>
            {t('buyCredits.desc')}
          </DialogDescription>
        </DialogHeader>

        {packsQ.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('buyCredits.loadingPacks')}</p>
        ) : !packsQ.data || packs.length === 0 ? (
          <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            {t('buyCredits.notForSale')}
          </p>
        ) : (
          <div className="space-y-4">
            <div className={cn('grid grid-cols-2 gap-3', packs.length >= 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3')} role="radiogroup" aria-label={t('buyCredits.packsAria')}>
              {packs.map((p) => {
                const on = p.id === pack?.id
                const bonus = packBonusPercent(p, rate?.creditValueVnd)
                const turns = packTurns(p, rate)
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setPicked(p.id)}
                    className={cn(
                      'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors',
                      on ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50',
                    )}
                  >
                    <span className="text-lg font-semibold tabular-nums">{formatTurns(turns)}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">{formatDong(p.price)}</span>
                    {bonus > 0 ? (
                      <Badge variant="success" className="mt-1">{t('buyCredits.bonus', { percent: bonus })}</Badge>
                    ) : (
                      <Badge variant="secondary" className="mt-1">{t('buyCredits.basePrice')}</Badge>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="overflow-hidden rounded-lg border text-sm">
              {q?.promo && (
                <Line
                  label={<span>{t('buyCredits.promoCode', { code: q.promo.code })}</span>}
                  value={<span className="text-success">−{formatDong(q.promo.amount)}</span>}
                />
              )}
              <Line label={t('buyCredits.subtotal')} value={formatDong(subtotal)} />
              <Line label={vatLabel(vatPercent)} value={formatDong(vat)} />
              <Line label={<b>{t('buyCredits.total')}</b>} value={<b className="text-primary">{formatDong(total)}</b>} />
              {credits && pack && (
                <Line
                  label={<span className="text-muted-foreground">{t('buyCredits.balanceAfter')}</span>}
                  value={<span className="text-muted-foreground">{amountText(credits.balance + pack.credits, rate)}</span>}
                />
              )}
            </div>
            {quote.isError && <p className="text-xs text-destructive">{aiErrorMessage(quote.error)}</p>}
            <PromoCodeBox
              applied={promoCode}
              promo={q?.promo ?? null}
              note={q?.promoNote ?? null}
              error={q?.promoError ?? null}
              checking={checkingPromo}
              onApply={setPromoCode}
              disabled={busy}
            />

            <div className="space-y-1.5">
              <p className="text-sm font-medium">{t('buyCredits.paymentMethod')}</p>
              {methods.isLoading ? (
                <p className="text-sm text-muted-foreground">{t('buyCredits.loadingMethods')}</p>
              ) : method ? (
                <div className="space-y-2" role="radiogroup" aria-label={t('buyCredits.paymentMethod')}>
                  {(methods.data?.methods ?? []).map((m) => {
                    const on = m.id === method.id
                    const copy = METHOD_COPY[m.id]
                    return (
                      <button
                        key={m.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setChosenMethod(m.id)}
                        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm ${on ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p><span className="font-medium">{copy?.title ?? m.label}</span>{copy?.hint ? <span className="text-muted-foreground"> {copy.hint}</span> : null}</p>
                        </div>
                        <MethodLogos id={m.id} className="hidden shrink-0 items-center gap-1 sm:flex" />
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                  {t('buyCredits.onlineOff')}
                </p>
              )}
            </div>
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              {t('buyCredits.vatNote')}
            </p>
            {!canPay && <p className="text-xs text-muted-foreground">{t('buyCredits.noPermission')}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{t('common:actions.cancel')}</Button>
          <Button onClick={pay} disabled={!canPay || !pack || !method || busy || quote.isError || promoBlocked || checkingPromo}>
            {busy && <Loader2 className="animate-spin" />}
            {redirecting ? t('buyCredits.redirecting') : t('buyCredits.pay')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Line({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b px-3 py-2 last:border-b-0">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}
